import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { toast } from "sonner";

import { getOwnProfile } from "@/api/settings";
import { settings as copy } from "@/content";
import { useSessionState } from "@/context/UserContext";
import { saveProfile } from "@/hooks/api/system";
import {
  applyLocale,
  getActiveLocale,
  isLocale,
  readLocale,
  writeLocale,
  type Locale,
} from "@/utils/locale";

type LocaleContextValue = {
  locale: Locale;
  pending: boolean;
  setLocale: (locale: Locale) => void;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

/**
 * Keeps the module-level active locale (content Proxies) and React tree in sync.
 *
 * Persistence lives here (not in LanguageSwitch) so a remount of the keyed
 * surface does not cancel the in-flight `saveProfile`.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const sessionState = useSessionState();
  const sessionRef = useRef(sessionState);
  sessionRef.current = sessionState;

  const [locale, setLocaleState] = useState<Locale>(() => readLocale());
  const [pending, setPending] = useState(false);
  const seenAccountLocale = useRef<Locale | undefined>(undefined);

  const accountLocale =
    sessionState.status === "signedIn" ? sessionState.session.user.locale : undefined;

  useEffect(() => {
    applyLocale(locale);
  }, [locale]);

  /* Adopt `users.locale` when the account value itself changes (sign-in or
     post-save refresh). Do not re-apply the same account value while the UI has
     already moved ahead of a pending save. */
  useEffect(() => {
    if (!isLocale(accountLocale)) return;
    if (seenAccountLocale.current === accountLocale) return;
    seenAccountLocale.current = accountLocale;
    if (accountLocale === getActiveLocale()) return;
    writeLocale(accountLocale);
    setLocaleState(accountLocale);
  }, [accountLocale]);

  const setLocale = useCallback((next: Locale) => {
    if (next === getActiveLocale()) return;
    writeLocale(next);
    setLocaleState(next);

    const session = sessionRef.current;
    if (session.status !== "signedIn") return;

    setPending(true);
    void (async () => {
      try {
        const profile = await getOwnProfile();
        if (!profile) {
          toast.error(copy.errors.generic);
          return;
        }
        const formData = new FormData();
        formData.set("name", profile.name);
        formData.set("phone", profile.phone ?? "");
        formData.set("numeralSystem", profile.numeralSystem);
        formData.set("locale", next);
        const result = await saveProfile(formData);
        if (!result.ok) {
          toast.error(result.error);
          return;
        }
        await session.refresh();
      } catch {
        toast.error(copy.errors.generic);
      } finally {
        setPending(false);
      }
    })();
  }, []);

  const value = useMemo(
    () => ({ locale, pending, setLocale }),
    [locale, pending, setLocale],
  );

  /* Remount on locale change so React Compiler–memoized screens that never call
     `useLocale()` still re-read the content Proxies. */
  return (
    <LocaleContext.Provider value={value}>
      <Fragment key={locale}>{children}</Fragment>
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    return {
      locale: getActiveLocale(),
      pending: false,
      setLocale: writeLocale,
    };
  }
  return ctx;
}
